/**
 * MemoryWallPreviewTile — quiet, ambient host-side glance at the live
 * Memory Wall. Per product direction:
 *
 *   • feels like "glancing over at memories appearing"
 *   • NOT "monitoring uploads"
 *   • no counts, no admin buttons, no moderation language
 *   • 4–6 most-recent polaroids drifting in place
 *   • parallax sway, shadow breathing, gentle gold light bloom
 *   • new additions enter with a hand-pinning fade-in (no jump to top)
 *
 * Polls every 12s as a fallback. SSE upgrade can be wired later by
 * swapping `useInterval` for an EventSource on the same data shape.
 *
 * Auto-hides cleanly when the event doesn't own `memory_snap` —
 * EventPage already gates the tile, but we double-check here so a
 * direct render in a different surface won't leak.
 */

import React, { useEffect, useRef, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Polaroid from './Polaroid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 12_000;
const MAX_VISIBLE = 6;

const absoluteUrl = (u) => (u && !u.startsWith('http') ? `${BACKEND_URL}${u}` : u);

// Deterministic per-snap drift parameters so each polaroid sways on
// its own gentle loop without the wall feeling synchronised. We hash
// the snap_id into a small offset/duration window so the motion looks
// organic but stays restrained (≤2px translate, ≤0.4° rotate).
const driftParams = (id) => {
  let h = 0;
  for (let i = 0; i < (id || '').length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return {
    delay: -((h % 700) / 100),               // 0–7s negative delay → desync start
    duration: 7 + ((h >>> 3) % 500) / 100,    // 7–12s drift loop
    direction: (h & 1) ? 1 : -1,
  };
};

export const MemoryWallPreviewTile = ({ event }) => {
  const navigate = useNavigate();
  const [snaps, setSnaps] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const knownIdsRef = useRef(new Set());
  const [recentIds, setRecentIds] = useState(new Set());

  useEffect(() => {
    if (!event?.event_id) return undefined;
    if (!Array.isArray(event.features) || !event.features.includes('memory_snap')) return undefined;
    let cancelled = false;
    const pull = async () => {
      try {
        const r = await axios.get(`${API}/events/${event.event_id}/memory-snaps?limit=${MAX_VISIBLE}`);
        if (cancelled) return;
        const incoming = (r.data?.snaps || []).slice(0, MAX_VISIBLE).map((s) => ({
          ...s, photo_url: absoluteUrl(s.photo_url),
        }));
        // Detect fresh additions — only after the first hydration so we
        // don't fade every existing snap on initial mount.
        if (hasFetched) {
          const fresh = incoming
            .filter((s) => !knownIdsRef.current.has(s.snap_id))
            .map((s) => s.snap_id);
          if (fresh.length) {
            setRecentIds((prev) => {
              const next = new Set(prev);
              fresh.forEach((id) => next.add(id));
              return next;
            });
            // Retire the "recent" glow after the entry animation completes.
            setTimeout(() => {
              setRecentIds((prev) => {
                const next = new Set(prev);
                fresh.forEach((id) => next.delete(id));
                return next;
              });
            }, 2400);
          }
        }
        incoming.forEach((s) => knownIdsRef.current.add(s.snap_id));
        setSnaps(incoming);
        setHasFetched(true);
      } catch { /* quiet; tile stays in its previous state */ }
    };
    pull();
    const iv = setInterval(pull, POLL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [event?.event_id, event?.features, hasFetched]);

  const featureOwned = useMemo(
    () => Array.isArray(event?.features) && event.features.includes('memory_snap'),
    [event?.features]
  );

  // Hide until the feature is owned AND there's at least one memory.
  // The tile must never feel like an empty operational widget — when
  // there's nothing to glance at, glance at nothing.
  if (!featureOwned) return null;
  if (hasFetched && snaps.length === 0) return null;

  return (
    <div
      data-testid="memory-wall-preview-tile"
      className="mb-8"
      style={{
        position: 'relative',
        borderRadius: 22,
        overflow: 'hidden',
        background: `
          radial-gradient(ellipse 90% 60% at 50% 0%, rgba(212,175,94,0.10), transparent 70%),
          radial-gradient(ellipse 80% 60% at 80% 100%, rgba(60,30,15,0.30), transparent 75%),
          linear-gradient(180deg, #1c1108 0%, #120a05 100%)
        `,
        border: '1px solid rgba(212,175,94,0.16)',
        boxShadow: '0 24px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(212,175,94,0.08)',
        padding: '22px 18px 18px',
        cursor: 'pointer',
      }}
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/event/${event.slug}/memory-wall`)}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/event/${event.slug}/memory-wall`); }}
    >
      {/* Top label — small, calm, no metrics. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <p
          data-testid="memory-wall-preview-label"
          style={{
            margin: 0, fontSize: 9, fontWeight: 800,
            letterSpacing: '0.32em', textTransform: 'uppercase',
            color: 'rgba(212,175,94,0.78)',
          }}
        >✦ Memory Wall</p>
        <span
          aria-hidden
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 10, fontWeight: 600,
            color: 'rgba(255,255,255,0.42)', letterSpacing: '0.06em',
          }}
        >
          <span style={{
            width: 5, height: 5, borderRadius: '50%',
            background: '#5EE17B',
            boxShadow: '0 0 8px rgba(94,225,123,0.55)',
            animation: 'memoryTilePulse 2.4s ease-in-out infinite',
          }} />
          live
        </span>
      </div>

      {/* Drifting polaroid row — 4–6 most-recent memories, gently
          floating in place. Horizontal scroll snap on overflow so the
          tile gracefully scales when the wall fills up. */}
      <div
        data-testid="memory-wall-preview-row"
        style={{
          position: 'relative',
          display: 'flex',
          gap: 14,
          overflowX: 'auto',
          paddingBottom: 8,
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
          msOverflowStyle: 'none',
        }}
      >
        {snaps.map((s) => {
          const drift = driftParams(s.snap_id);
          const isRecent = recentIds.has(s.snap_id);
          return (
            <div
              key={s.snap_id}
              data-testid={`memory-wall-preview-snap-${s.snap_id}`}
              style={{
                flex: '0 0 auto',
                transform: `rotate(${s.wall?.rotation ? s.wall.rotation * 0.5 : 0}deg)`,
                animation: `${isRecent ? 'memoryTilePin 1.4s cubic-bezier(0.16,1,0.3,1) forwards, ' : ''}memoryTileDrift ${drift.duration}s ease-in-out ${drift.delay}s infinite`,
                filter: isRecent
                  ? 'drop-shadow(0 18px 28px rgba(212,175,94,0.32))'
                  : 'drop-shadow(0 16px 24px rgba(0,0,0,0.45))',
                transition: 'filter 1.4s ease-out',
                willChange: 'transform, filter',
              }}
            >
              <Polaroid
                photoUrl={s.photo_url}
                message={s.message}
                penColor={s.pen_hex}
                seed={s.style_seed || 1}
                width={140}
                tape={s.wall?.tape || 'washi'}
                showCorners={false}
              />
            </div>
          );
        })}
      </div>

      {/* Quiet bottom link — the only operational element in the tile.
          Reads as an invitation, not a CTA button. */}
      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <span
          data-testid="memory-wall-preview-open"
          style={{
            fontSize: 11, fontWeight: 600,
            color: 'rgba(212,175,94,0.78)',
            letterSpacing: '0.04em',
          }}
        >Open Memory Wall →</span>
      </div>

      <style>{`
        @keyframes memoryTilePulse {
          0%, 100% { opacity: 0.55; transform: scale(0.92); }
          50%      { opacity: 1;    transform: scale(1.12); }
        }
        @keyframes memoryTileDrift {
          0%   { transform: translate3d(0, 0, 0)        rotate(var(--rot, 0deg)); }
          50%  { transform: translate3d(0, -2px, 0)     rotate(calc(var(--rot, 0deg) + 0.4deg)); }
          100% { transform: translate3d(0, 0, 0)        rotate(var(--rot, 0deg)); }
        }
        @keyframes memoryTilePin {
          0%   { transform: translateY(-10px) rotate(-1deg) scale(0.92); opacity: 0; }
          60%  { transform: translateY(2px)   rotate(0.5deg) scale(1.04); opacity: 1; }
          100% { transform: translateY(0)     rotate(0deg)   scale(1);     opacity: 1; }
        }
        [data-testid="memory-wall-preview-row"]::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

export default MemoryWallPreviewTile;
